let baseUrl = '';
let accessTokenGetter: (() => Promise<string | undefined>) | undefined;

export function setBaseUrl(url: string): void {
  baseUrl = url.replace(/\/$/, '');
}

export function getBaseUrl(): string {
  return baseUrl;
}

/**
 * Configure how the client retrieves the Bearer access token.
 * Call this at app startup with your SuperTokens session accessor.
 */
export function setAccessTokenGetter(getter: () => Promise<string | undefined>): void {
  accessTokenGetter = getter;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/** Default number of retry attempts for transient failures. */
const DEFAULT_MAX_RETRIES = 3;

/**
 * React Query `retry` predicate that only retries transient failures.
 *
 * Permanent errors (most 4xx — bad request, unauthorized, not found, …) reflect
 * a request that will fail identically no matter how many times it is repeated,
 * so they are surfaced immediately rather than retried. Transient failures
 * (network errors with no `ApiError` status, 5xx server errors, and the two
 * retry-friendly 4xx codes 408/429) are retried up to `maxRetries` times.
 */
export function shouldRetryRequest(
  failureCount: number,
  error: unknown,
  maxRetries = DEFAULT_MAX_RETRIES
): boolean {
  if (failureCount >= maxRetries) {
    return false;
  }

  // No HTTP status means a network/connection failure (fetch rejected) —
  // treat as transient.
  if (!(error instanceof ApiError)) {
    return true;
  }

  const { status } = error;

  // Request Timeout and Too Many Requests are retry-friendly 4xx codes.
  if (status === 408 || status === 429) {
    return true;
  }

  // Other client errors (4xx) are permanent; server errors (5xx) are transient.
  return status >= 500;
}

export async function customFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);

  // Do not force application/json on FormData bodies — the browser must set
  // multipart/form-data with its own boundary, or the upload is unparseable server-side.
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (accessTokenGetter) {
    const token = await accessTokenGetter();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;

  const response = await fetch(fullUrl, { ...options, headers, credentials: 'include' });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type');
  const data = contentType?.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw new ApiError(`API error ${response.status}`, response.status, data);
  }

  return data as T;
}
